import type { Persona, Rule, UserPersonaDraft, UserRuleDraft } from "./types";

/**
 * User rules, rule order, system-rule overrides, and persona definitions
 * persist in `OfficeRuntime.storage` — per user, per device. They persist
 * immediately and survive across all workbooks the user opens.
 *
 * Per-workbook state — currently "which persona is active for this file" —
 * lives in `Office.context.document.settings` instead, and travels with the
 * .xlsx (and only persists when the user saves the workbook).
 */

const USER_RULES_KEY = "cogseven.toolkit.userRules";
const RULE_ORDER_KEY = "cogseven.toolkit.ruleOrder";
const SYSTEM_OVERRIDES_KEY = "cogseven.toolkit.systemRuleOverrides";
const PERSONAS_KEY = "cogseven.toolkit.personas";
const ACTIVE_PERSONA_KEY = "cogseven.toolkit.activePersonaId";

const MIGRATION_MARKER_KEY = "cogseven.toolkit.migratedFromOfficeRuntime";
const MIGRATION_TIMEOUT_MS = 2000;

export interface SystemRuleOverride {
  enabled?: boolean;
}

/**
 * Storage uses `localStorage` directly: synchronous, reliable, per-user,
 * per-device, persists immediately. We previously used `OfficeRuntime.storage`
 * but observed reads hanging indefinitely (especially when two task panes are
 * open at once), so we migrated. Function signatures stay async so existing
 * callers don't need to change.
 *
 * On first call after this change, `ensureMigrated` does a one-shot
 * best-effort copy from `OfficeRuntime.storage` so existing data isn't lost.
 */

let migrationPromise: Promise<void> | null = null;

function ensureMigrated(): Promise<void> {
  if (!migrationPromise) migrationPromise = migrateFromOfficeRuntime();
  return migrationPromise;
}

async function migrateFromOfficeRuntime(): Promise<void> {
  if (localStorage.getItem(MIGRATION_MARKER_KEY) === "true") return;

  const keysToMigrate = [USER_RULES_KEY, RULE_ORDER_KEY, SYSTEM_OVERRIDES_KEY, PERSONAS_KEY];
  const keysNeeding = keysToMigrate.filter((k) => localStorage.getItem(k) === null);

  if (keysNeeding.length === 0) {
    localStorage.setItem(MIGRATION_MARKER_KEY, "true");
    return;
  }

  await Promise.all(
    keysNeeding.map(async (key) => {
      try {
        const value = await Promise.race<string | null>([
          OfficeRuntime.storage.getItem(key),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), MIGRATION_TIMEOUT_MS)),
        ]);
        if (value !== null) {
          localStorage.setItem(key, value);
        }
      } catch {
        // best-effort: ignore migration failures
      }
    })
  );

  localStorage.setItem(MIGRATION_MARKER_KEY, "true");
}

async function storageGet(key: string): Promise<string | null> {
  await ensureMigrated();
  return localStorage.getItem(key);
}

async function storageSet(key: string, value: string): Promise<void> {
  await ensureMigrated();
  localStorage.setItem(key, value);
}

export async function loadUserRules(): Promise<Rule[]> {
  const raw = await storageGet(USER_RULES_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidUserRule);
  } catch {
    return [];
  }
}

export async function saveUserRules(rules: Rule[]): Promise<void> {
  await storageSet(USER_RULES_KEY, JSON.stringify(rules));
}

export async function addUserRule(draft: UserRuleDraft): Promise<Rule> {
  const rule: Rule = {
    ...draft,
    id: generateId(),
    source: "user",
  };
  const rules = await loadUserRules();
  rules.push(rule);
  await saveUserRules(rules);
  return rule;
}

export async function updateUserRule(
  id: string,
  updates: Partial<Omit<Rule, "id" | "source">>
): Promise<void> {
  const rules = await loadUserRules();
  const idx = rules.findIndex((r) => r.id === id);
  if (idx === -1) throw new Error(`updateUserRule: id not found (${id})`);
  rules[idx] = { ...rules[idx], ...updates, source: "user", id };
  await saveUserRules(rules);
}

export async function deleteUserRule(id: string): Promise<void> {
  const remaining = (await loadUserRules()).filter((r) => r.id !== id);
  await saveUserRules(remaining);
}

export async function loadRuleOrder(): Promise<string[]> {
  const raw = await storageGet(RULE_ORDER_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x) => typeof x === "string") ? parsed : [];
  } catch {
    return [];
  }
}

export async function saveRuleOrder(orderIds: string[]): Promise<void> {
  await storageSet(RULE_ORDER_KEY, JSON.stringify(orderIds));
}

export async function loadSystemRuleOverrides(): Promise<Record<string, SystemRuleOverride>> {
  const raw = await storageGet(SYSTEM_OVERRIDES_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const out: Record<string, SystemRuleOverride> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "object" && value !== null) {
        out[key] = value as SystemRuleOverride;
      }
    }
    return out;
  } catch {
    return {};
  }
}

export async function saveSystemRuleOverrides(
  overrides: Record<string, SystemRuleOverride>
): Promise<void> {
  await storageSet(SYSTEM_OVERRIDES_KEY, JSON.stringify(overrides));
}

export async function loadPersonas(): Promise<Persona[]> {
  const raw = await storageGet(PERSONAS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidPersona).map((p) => normalizePersona(p as Persona));
  } catch {
    return [];
  }
}

function normalizePersona(p: Persona): Persona {
  return { ...p, ruleIds: Array.isArray(p.ruleIds) ? p.ruleIds : [] };
}

export async function savePersonas(personas: Persona[]): Promise<void> {
  await storageSet(PERSONAS_KEY, JSON.stringify(personas));
}

export async function addPersona(draft: UserPersonaDraft): Promise<Persona> {
  const persona: Persona = {
    ...draft,
    id: generateId("persona"),
    source: "user",
  };
  const personas = await loadPersonas();
  personas.push(persona);
  await savePersonas(personas);
  return persona;
}

export async function updatePersona(
  id: string,
  updates: Partial<Omit<Persona, "id" | "source">>
): Promise<void> {
  const personas = await loadPersonas();
  const idx = personas.findIndex((p) => p.id === id);
  if (idx === -1) throw new Error(`updatePersona: id not found (${id})`);
  personas[idx] = { ...personas[idx], ...updates, source: "user", id };
  await savePersonas(personas);
}

export async function deletePersona(id: string): Promise<void> {
  const remaining = (await loadPersonas()).filter((p) => p.id !== id);
  await savePersonas(remaining);
}

/**
 * Active persona for the current workbook. Null = no persona.
 *
 * Persistence uses Office.context.document.settings which is per-workbook
 * AND only available in the task-pane context. Dialog windows (Reformat
 * dialog, etc.) have no document context and these calls would crash —
 * we return null / no-op safely so the dialog can still function.
 */
function getDocSettings(): Office.Settings | null {
  // Office.context.document is undefined in dialog windows.
  const doc = (Office.context as { document?: Office.Document }).document;
  return doc?.settings ?? null;
}

export function loadActivePersonaId(): string | null {
  const settings = getDocSettings();
  if (!settings) return null;
  const raw = settings.get(ACTIVE_PERSONA_KEY);
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

export async function saveActivePersonaId(id: string | null): Promise<void> {
  const settings = getDocSettings();
  if (!settings) return; // dialog context — no document to persist to
  if (id === null) {
    settings.remove(ACTIVE_PERSONA_KEY);
  } else {
    settings.set(ACTIVE_PERSONA_KEY, id);
  }
  await new Promise<void>((resolve, reject) => {
    settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(new Error(`saveAsync failed: ${result.error?.message ?? "unknown"}`));
      }
    });
  });
}

function generateId(prefix = "rule"): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function isValidUserRule(x: unknown): x is Rule {
  if (typeof x !== "object" || x === null) return false;
  const r = x as Record<string, unknown>;
  return (
    typeof r.id === "string" &&
    typeof r.name === "string" &&
    typeof r.enabled === "boolean" &&
    r.source === "user" &&
    typeof r.match === "object" &&
    r.match !== null &&
    typeof r.apply === "object" &&
    r.apply !== null
  );
}

function isValidPersona(x: unknown): x is Persona {
  if (typeof x !== "object" || x === null) return false;
  const p = x as Record<string, unknown>;
  if (typeof p.id !== "string") return false;
  if (typeof p.name !== "string") return false;
  if (p.source !== "user" && p.source !== "system") return false;
  // ruleIds is optional in storage; normalizePersona defaults missing/invalid to [].
  if (p.ruleIds !== undefined) {
    if (!Array.isArray(p.ruleIds)) return false;
    if (!p.ruleIds.every((id) => typeof id === "string")) return false;
  }
  return true;
}
